/**
 * Flow (funnel) analytics — the five events a multi-step flow needs so a
 * PostHog funnel can be built without bespoke wiring per feature.
 *
 * Generalized from the onboarding funnel
 * (`src/apps/moon/onboarding/analytics.ts`), which was the only flow that had
 * this shape. Every flow emits the same five verbs under its own prefix:
 *
 *   <flow>_started
 *   <flow>_step_viewed      { step, ... }
 *   <flow>_step_submitted   { step, ... }
 *   <flow>_completed
 *   <flow>_abandoned        { reason, ... }
 *
 * Naming: `flow` is a lowercase snake_case name for what the user is doing,
 * not for the screen it happens on (`medical_history`, `plan_builder`,
 * `invite_accept`, `coverage_estimate`). Where a flow has variants, prefer a
 * property over a new flow name — one `medical_history` flow with a
 * `section` property, not one flow per section — so event-name cardinality
 * stays flat and the funnel can be sliced instead of duplicated.
 *
 * Props are segmentation dimensions known at fire time: enums, booleans,
 * counts, stable ids of *content* (a section key, a modality). Never free
 * text the user typed, never a clinical value, never a record id.
 *
 * Like `usePressCapture`, every call is optional-chained: `usePostHog`
 * resolves to undefined outside a `PostHogProvider` (tests, the design-system
 * app), so callers can fire freely.
 */
import { useMemo } from "react";
import { usePostHog } from "posthog-react-native";

/** Segmentation dimensions. Never free text, clinical values, or record ids. */
export type FlowProps = Record<string, string | number | boolean | null>;

export type FlowAnalytics = {
  started: (props?: FlowProps) => void;
  stepViewed: (step: string, props?: FlowProps) => void;
  stepSubmitted: (step: string, props?: FlowProps) => void;
  completed: (props?: FlowProps) => void;
  abandoned: (reason: string, props?: FlowProps) => void;
};

export function useFlowAnalytics(flow: string): FlowAnalytics {
  const posthog = usePostHog();

  return useMemo<FlowAnalytics>(
    () => ({
      started: (props) => {
        posthog?.capture(`${flow}_started`, { ...props });
      },
      stepViewed: (step, props) => {
        posthog?.capture(`${flow}_step_viewed`, { step, ...props });
      },
      stepSubmitted: (step, props) => {
        posthog?.capture(`${flow}_step_submitted`, { step, ...props });
      },
      completed: (props) => {
        posthog?.capture(`${flow}_completed`, { ...props });
      },
      abandoned: (reason, props) => {
        posthog?.capture(`${flow}_abandoned`, { reason, ...props });
      },
    }),
    [posthog, flow],
  );
}
